import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth as useAuth0 } from '@/hooks/useAdminAuth';
import { Plus, Trash2, Edit3, Languages, Check, Clock, X, RefreshCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANGS = ["en", "de", "es", "fr", "pt", "zh", "ar", "hi", "ru", "ja"];

export default function TranslationQueue() {
  const { getAccessTokenSilently } = useAuth0();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [filter, setFilter] = useState('');

  const auth = useCallback(async () => {
    const t = await getAccessTokenSilently();
    return { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' };
  }, [getAccessTokenSilently]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const h = await auth();
      const r = await fetch(`${API}/admin/translation-queue?limit=100${filter ? `&status=${filter}` : ''}`, { headers: h });
      const d = await r.json();
      setItems(d.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [auth, filter]);

  useEffect(() => { load(); }, [load]);

  async function createItem(body) {
    const h = await auth();
    await fetch(`${API}/admin/translation-queue`, { method: 'POST', headers: h, body: JSON.stringify(body) });
    setShowCreate(false);
    load();
  }
  async function translateOne(id) {
    const h = await auth();
    await fetch(`${API}/admin/translation-queue/${id}/translate`, { method: 'POST', headers: h });
    setTimeout(load, 1500);
  }
  async function batchTranslate() {
    if (!window.confirm('Prevedi sve neprevedene stavke?')) return;
    const h = await auth();
    await fetch(`${API}/admin/translation-queue/batch-translate?status=pending`, { method: 'POST', headers: h });
    setTimeout(load, 1500);
  }
  async function del(id) {
    if (!window.confirm('Obriši stavku?')) return;
    const h = await auth();
    await fetch(`${API}/admin/translation-queue/${id}`, { method: 'DELETE', headers: h });
    load();
  }
  async function saveEdit(id, body) {
    const h = await auth();
    await fetch(`${API}/admin/translation-queue/${id}`, { method: 'PUT', headers: h, body: JSON.stringify(body) });
    setEditItem(null);
    load();
  }

  return (
    <div className="p-6" data-testid="translation-queue-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold">Neprevedeni sadržaj</h1>
          <p className="text-xs text-zinc-400 mt-1">Ručno upravljanje prijevodima URL-ova, copyja oglasa i ostalih stringova.</p>
        </div>
        <div className="flex gap-2">
          <select value={filter} onChange={e => setFilter(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-1.5 text-sm" data-testid="tq-filter">
            <option value="">Svi statusi</option>
            <option value="pending">Pending</option>
            <option value="translating">Translating</option>
            <option value="done">Done</option>
            <option value="failed">Failed</option>
          </select>
          <button onClick={load} className="px-3 py-1.5 text-sm border border-zinc-700 rounded-md hover:bg-zinc-800 flex items-center gap-1.5" data-testid="tq-reload"><RefreshCcw size={12} /> Osvježi</button>
          <button onClick={batchTranslate} className="px-3 py-1.5 text-sm bg-amber-600 hover:bg-amber-700 rounded-md text-white flex items-center gap-1.5" data-testid="tq-batch">
            <Languages size={12} /> Prevedi sve neprevedene
          </button>
          <button onClick={() => setShowCreate(true)} className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 rounded-md text-white flex items-center gap-1.5" data-testid="tq-add">
            <Plus size={12} /> Dodaj stavku
          </button>
        </div>
      </div>

      <div className="border border-zinc-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-zinc-400 text-left text-xs">
            <tr>
              <th className="px-3 py-2">Oznaka</th>
              <th className="px-3 py-2">Izvor</th>
              <th className="px-3 py-2">Ciljani jezici</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Akcije</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8 text-zinc-500">Učitavam…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-8 text-zinc-500">Nema stavki.</td></tr>
            ) : items.map(it => (
              <tr key={it.id} className="border-t border-zinc-800" data-testid={`tq-row-${it.id}`}>
                <td className="px-3 py-2 font-medium">{it.label}</td>
                <td className="px-3 py-2 text-zinc-400 max-w-xs truncate" title={it.source_text}>{(it.source_text || '').slice(0, 80)}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(it.target_langs || []).map(l => {
                      const done = !!(it.translations || {})[l];
                      return (
                        <span key={l} className={`text-[10px] px-1.5 py-0.5 rounded border ${done ? 'bg-emerald-900/40 text-emerald-300 border-emerald-800' : 'bg-amber-900/40 text-amber-300 border-amber-800'}`}>
                          {l.toUpperCase()} {done ? '✓' : '⏳'}
                        </span>
                      );
                    })}
                  </div>
                </td>
                <td className="px-3 py-2"><StatusBadge status={it.status} /></td>
                <td className="px-3 py-2 text-right space-x-1">
                  <button onClick={() => translateOne(it.id)} className="p-1.5 text-amber-400 hover:text-amber-300" title="Prevedi" data-testid={`tq-translate-${it.id}`}><Languages size={14} /></button>
                  <button onClick={() => setViewItem(it)} className="p-1.5 text-blue-400 hover:text-blue-300" title="Pregled" data-testid={`tq-view-${it.id}`}><Check size={14} /></button>
                  <button onClick={() => setEditItem(it)} className="p-1.5 text-zinc-400 hover:text-zinc-200" title="Uredi" data-testid={`tq-edit-${it.id}`}><Edit3 size={14} /></button>
                  <button onClick={() => del(it.id)} className="p-1.5 text-red-400 hover:text-red-300" title="Obriši" data-testid={`tq-delete-${it.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onSave={createItem} />}
      {viewItem && <ViewModal item={viewItem} onClose={() => setViewItem(null)} />}
      {editItem && <EditModal item={editItem} onClose={() => setEditItem(null)} onSave={saveEdit} />}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: ['bg-amber-900/40 text-amber-300', <Clock key="c" size={10} />],
    translating: ['bg-blue-900/40 text-blue-300', <Languages key="l" size={10} />],
    done: ['bg-emerald-900/40 text-emerald-300', <Check key="c" size={10} />],
    failed: ['bg-red-900/40 text-red-300', <X key="x" size={10} />],
  };
  const [cls, icon] = map[status] || ['bg-zinc-800 text-zinc-300', null];
  return <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded ${cls}`}>{icon} {status}</span>;
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function CreateModal({ onClose, onSave }) {
  const [label, setLabel] = useState('');
  const [text, setText] = useState('');
  const [targets, setTargets] = useState([...LANGS]);
  const [url, setUrl] = useState('');
  function toggle(l) { setTargets(t => t.includes(l) ? t.filter(x => x !== l) : [...t, l]); }
  return (
    <Modal title="Nova stavka za prijevod" onClose={onClose} testid="tq-create-modal">
      <form onSubmit={(e) => { e.preventDefault(); onSave({ label, source_text: text, target_langs: targets, target_url: url || null }); }}>
        <label className="block text-xs text-zinc-400 mb-1">Oznaka</label>
        <input value={label} onChange={e => setLabel(e.target.value)} required className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm mb-3" data-testid="tq-create-label" />
        <label className="block text-xs text-zinc-400 mb-1">Izvorni tekst (HR)</label>
        <textarea value={text} onChange={e => setText(e.target.value)} required rows={4} className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm mb-3" data-testid="tq-create-text" />
        <label className="block text-xs text-zinc-400 mb-1">URL (opcionalno)</label>
        <input value={url} onChange={e => setUrl(e.target.value)} className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm mb-3" data-testid="tq-create-url" />
        <label className="block text-xs text-zinc-400 mb-1">Ciljani jezici</label>
        <div className="flex flex-wrap gap-1 mb-4">
          {LANGS.map(l => (
            <button type="button" key={l} onClick={() => toggle(l)}
              className={`text-[10px] px-2 py-1 rounded border ${targets.includes(l) ? 'bg-red-600 text-white border-red-600' : 'bg-zinc-950 border-zinc-700 text-zinc-400'}`}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>
        <button type="submit" className="w-full py-2 bg-red-600 hover:bg-red-700 rounded-md font-medium" data-testid="tq-create-submit">Spremi</button>
      </form>
    </Modal>
  );
}

function ViewModal({ item, onClose }) {
  return (
    <Modal title={`Prijevodi: ${item.label}`} onClose={onClose} testid="tq-view-modal">
      <div className="mb-3">
        <p className="text-xs text-zinc-400">Izvor (HR)</p>
        <p className="text-sm bg-zinc-950 border border-zinc-800 rounded p-3 whitespace-pre-wrap">{item.source_text}</p>
      </div>
      <div className="max-h-80 overflow-y-auto space-y-2">
        {LANGS.map(l => (
          <div key={l} className="border border-zinc-800 rounded p-2">
            <p className="text-[10px] uppercase text-zinc-400 font-bold">{l}</p>
            <p className="text-sm whitespace-pre-wrap">{(item.translations || {})[l] || <span className="text-zinc-500 italic">– nije prevedeno –</span>}</p>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function EditModal({ item, onClose, onSave }) {
  const [label, setLabel] = useState(item.label || '');
  const [text, setText] = useState(item.source_text || '');
  const [translations, setTranslations] = useState({ ...(item.translations || {}) });
  return (
    <Modal title="Uredi stavku" onClose={onClose} testid="tq-edit-modal">
      <form onSubmit={(e) => { e.preventDefault(); onSave(item.id, { label, source_text: text, translations }); }}>
        <label className="block text-xs text-zinc-400 mb-1">Oznaka</label>
        <input value={label} onChange={e => setLabel(e.target.value)} className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm mb-3" />
        <label className="block text-xs text-zinc-400 mb-1">Izvorni tekst</label>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={3} className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-sm mb-3" />
        <p className="text-xs text-zinc-400 mb-1">Ručni prijevodi</p>
        <div className="max-h-72 overflow-y-auto space-y-2 mb-4">
          {LANGS.map(l => (
            <div key={l}>
              <span className="text-[10px] text-zinc-500 uppercase">{l}</span>
              <textarea value={translations[l] || ''} onChange={e => setTranslations(p => ({ ...p, [l]: e.target.value }))}
                rows={2} className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-2 py-1 text-xs" />
            </div>
          ))}
        </div>
        <button type="submit" className="w-full py-2 bg-red-600 hover:bg-red-700 rounded-md font-medium">Spremi</button>
      </form>
    </Modal>
  );
}
